// frontend/src/components/TwoFactorSettings.js
// Two-Factor Authentication settings management component

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import api from '../services/api';
import TwoFactorSetup from './TwoFactorSetup';

const TwoFactorSettings = () => {
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [password, setPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/2fa/status');
      
      if (response.data.success) {
        setStatus(response.data.status);
      }
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
      setError('Failed to load 2FA settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!password) {
      setError('Password is required to disable 2FA');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const response = await api.post('/2fa/disable', { password });
      
      if (response.data.success) {
        setStatus({ ...status, enabled: false });
        setShowDisable(false);
        setPassword('');
        alert('Two-factor authentication has been disabled');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to disable 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateBackupCodes = async () => {
    if (!password) {
      setError('Password is required to generate new backup codes');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const response = await api.post('/2fa/backup-codes', { password });
      
      if (response.data.success) {
        setBackupCodes(response.data.backupCodes);
        setShowBackupCodes(true);
        setPassword('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate backup codes');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const content = backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pa-quiz-app-backup-codes.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
    fetchStatus(); // Refresh status
  };

  if (loading) {
    return (
      <div className="two-factor-settings">
        <div className="loading">Loading 2FA settings...</div>
      </div>
    );
  }

  if (showSetup) {
    return (
      <TwoFactorSetup
        onSetupComplete={handleSetupComplete}
        onCancel={() => setShowSetup(false)}
      />
    );
  }

  return (
    <div className="two-factor-settings">
      <div className="settings-header">
        <h3>Two-Factor Authentication</h3>
        <p>
          Add an extra layer of security to your account with two-factor authentication.
        </p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="status-section">
        <div className="status-info">
          <div className={`status-indicator ${status?.enabled ? 'enabled' : 'disabled'}`}>
            <span className="status-icon">
              {status?.enabled ? '🔐' : '🔓'}
            </span>
            <div className="status-text">
              <strong>
                {status?.enabled ? 'Enabled' : 'Disabled'}
              </strong>
              <p>
                {status?.enabled 
                  ? 'Your account is protected with 2FA'
                  : 'Your account is not protected with 2FA'
                }
              </p>
            </div>
          </div>

          {status?.enabled && (
            <div className="status-details">
              <div className="detail-item">
                <strong>Setup Date:</strong> {
                  status.setupDate 
                    ? new Date(status.setupDate).toLocaleDateString()
                    : 'Unknown'
                }
              </div>
              <div className="detail-item">
                <strong>Backup Codes Remaining:</strong> {status.backupCodesRemaining || 0}
              </div>
            </div>
          )}
        </div>

        <div className="actions">
          {!status?.enabled ? (
            <button
              onClick={() => setShowSetup(true)}
              className="btn btn-primary"
            >
              Enable 2FA
            </button>
          ) : (
            <div className="enabled-actions">
              <button
                onClick={() => setShowDisable(!showDisable)}
                className="btn btn-outline"
              >
                Disable 2FA
              </button>
              
              <button
                onClick={() => setShowBackupCodes(!showBackupCodes)}
                className="btn btn-secondary"
              >
                Generate New Backup Codes
              </button>
            </div>
          )}
        </div>
      </div>

      {showDisable && (
        <div className="action-form">
          <h4>Disable Two-Factor Authentication</h4>
          <p>
            Enter your password to confirm disabling 2FA. This will make your account less secure.
          </p>
          
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            className="form-input"
          />
          
          <div className="form-actions">
            <button
              onClick={handleDisable}
              disabled={actionLoading || !password}
              className="btn btn-danger"
            >
              {actionLoading ? 'Disabling...' : 'Disable 2FA'}
            </button>
            <button
              onClick={() => {
                setShowDisable(false);
                setPassword('');
                setError(null);
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showBackupCodes && (
        <div className="action-form">
          <h4>Generate New Backup Codes</h4>
          <p>
            Enter your password to generate new backup codes. Your old backup codes will no longer work.
          </p>
          
          {backupCodes.length === 0 ? (
            <>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="form-input"
              />
              
              <div className="form-actions">
                <button
                  onClick={handleGenerateBackupCodes}
                  disabled={actionLoading || !password}
                  className="btn btn-primary"
                >
                  {actionLoading ? 'Generating...' : 'Generate Backup Codes'}
                </button>
                <button
                  onClick={() => {
                    setShowBackupCodes(false);
                    setPassword('');
                    setError(null);
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="backup-codes-display">
                <p><strong>Your new backup codes:</strong></p>
                <div className="codes-grid">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="backup-code">
                      {code}
                    </div>
                  ))}
                </div>
                <p className="warning">
                  ⚠️ Store these codes in a safe place. Each code can only be used once.
                </p>
              </div>
              
              <div className="form-actions">
                <button
                  onClick={downloadBackupCodes}
                  className="btn btn-primary"
                >
                  Download Codes
                </button>
                <button
                  onClick={() => {
                    setShowBackupCodes(false);
                    setBackupCodes([]);
                    fetchStatus(); // Refresh status to update backup codes count
                  }}
                  className="btn btn-secondary"
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <style jsx>{`
        .two-factor-settings {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }

        .settings-header {
          margin-bottom: 30px;
        }

        .settings-header h3 {
          margin-bottom: 10px;
          color: #333;
        }

        .settings-header p {
          color: #666;
          line-height: 1.5;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 10px 15px;
          border-radius: 4px;
          margin-bottom: 20px;
          border: 1px solid #f5c6cb;
        }

        .status-section {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 30px;
        }

        .status-indicator {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
        }

        .status-icon {
          font-size: 24px;
          margin-right: 15px;
        }

        .status-text strong {
          display: block;
          font-size: 18px;
          margin-bottom: 5px;
        }

        .status-indicator.enabled .status-text strong {
          color: #28a745;
        }

        .status-indicator.disabled .status-text strong {
          color: #dc3545;
        }

        .status-text p {
          margin: 0;
          color: #666;
        }

        .status-details {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid #dee2e6;
        }

        .detail-item {
          margin-bottom: 8px;
          color: #666;
        }

        .detail-item strong {
          color: #333;
        }

        .actions {
          margin-top: 20px;
        }

        .enabled-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .action-form {
          background: #fff;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 20px;
          margin-top: 20px;
        }

        .action-form h4 {
          margin-bottom: 10px;
          color: #333;
        }

        .action-form p {
          color: #666;
          margin-bottom: 20px;
          line-height: 1.5;
        }

        .form-input {
          width: 100%;
          padding: 10px 15px;
          border: 1px solid #ddd;
          border-radius: 4px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .form-input:focus {
          outline: none;
          border-color: #007bff;
        }

        .form-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .backup-codes-display {
          margin-bottom: 20px;
        }

        .codes-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 10px;
          margin: 15px 0;
        }

        .backup-code {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          padding: 8px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 14px;
          font-weight: bold;
          text-align: center;
        }

        .warning {
          background: #fff3cd;
          color: #856404;
          padding: 10px 15px;
          border-radius: 4px;
          border: 1px solid #ffeaa7;
          margin-top: 15px;
        }

        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.2s;
          text-decoration: none;
          display: inline-block;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #0056b3;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #545b62;
        }

        .btn-outline {
          background: transparent;
          color: #007bff;
          border: 1px solid #007bff;
        }

        .btn-outline:hover:not(:disabled) {
          background: #007bff;
          color: white;
        }

        .btn-danger {
          background: #dc3545;
          color: white;
        }

        .btn-danger:hover:not(:disabled) {
          background: #c82333;
        }

        @media (max-width: 768px) {
          .two-factor-settings {
            padding: 15px;
          }

          .enabled-actions {
            flex-direction: column;
          }

          .form-actions {
            flex-direction: column;
          }

          .codes-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
};

export default TwoFactorSettings;